import {
  ChakraFlex,
  ChakraDivider,
  TagColor,
  Tag,
  Text,
  getGroupClassIconElement,
  SectionCard,
  SkeletonLoader,
} from '@equip.health/ui';

import RegistrationCriteria from '../common/RegistrationCriteria';
import { useEffect, useState } from 'react';
import type { GroupDetails } from '~/lib/util/groups.utils';
import { capitalizeFirstLetter } from '~/lib/common';
import { formatAgeRangeDisplay } from '~/lib/util/clinicalProfile.util';
import {
  useGetGroupTypesEnumQuery,
  useGetTreatmentModalitiesEnumQuery,
  useGetTreatmentProgramsQuery,
} from '~/store/services/enumerations';
import { GroupTypeTagColorMapper } from '~/lib/constants/groupClasses.constants';

interface GroupDetailsCardProps {
  groupDetails?: GroupDetails;
}

const convertGroupDetailsToFormState = (
  details: GroupDetails,
  treatmentPrograms: any[] = [],
  treatmentModalities: any[] = [],
  groupTypes: any[] = [],
): GroupDetails => ({
  id: details?.id,
  title: details?.title,
  description: details?.description,
  iconName: details?.iconName,
  groupTypeCode:
    groupTypes.find((gt) => gt.code === details?.groupTypeCode)?.type ??
    details?.groupTypeCode,
  groupTypeTagColor:
    GroupTypeTagColorMapper[
      groupTypes.find((gt) => gt.code === details?.groupTypeCode)?.code
    ],
  groupStatusCode: details?.groupStatusCode,
  treatmentPrograms:
    details?.treatmentPrograms?.map(
      (code) => treatmentPrograms.find((p) => p.code === code)?.label ?? code,
    ) ?? [],
  treatmentModalities:
    details?.treatmentModalities?.map(
      (code) => treatmentModalities.find((m) => m.code === code)?.label ?? code,
    ) ?? [],
  diagnoses: details?.diagnoses?.map((diagnosis) => diagnosis),
  ageRanges:
    details?.ageRanges?.map((range) => formatAgeRangeDisplay(range)) ?? [],
  attendeeRoles:
    details?.attendeeRoles?.map((role) =>
      capitalizeFirstLetter(String(role).toLowerCase()),
    ) ?? [],
  sensitive: details?.sensitive,
  core: details?.core,
});

const GroupDetailsCard = ({ groupDetails }: GroupDetailsCardProps) => {
  const TOP_LEVEL_ID = 'group-details-card';

  const [initialFormData, setInitialFormData] = useState<GroupDetails | null>(
    null,
  );

  const { data: treatmentPrograms } = useGetTreatmentProgramsQuery();
  const { data: treatmentModalities } = useGetTreatmentModalitiesEnumQuery();
  const { data: groupTypes } = useGetGroupTypesEnumQuery();

  useEffect(() => {
    if (groupDetails) {
      setInitialFormData(
        convertGroupDetailsToFormState(
          groupDetails,
          treatmentPrograms,
          treatmentModalities,
          groupTypes,
        ),
      );
    }
  }, [groupDetails, treatmentPrograms, treatmentModalities, groupTypes]);

  const {
    title,
    description,
    groupTypeCode,
    groupTypeTagColor,
    iconName: groupIcon,
    sensitive: isSensitive,
    core: isCore,
  } = initialFormData || {};

  return (
    <SkeletonLoader
      id={`${TOP_LEVEL_ID}__skeleton-loader`}
      isLoaded={!!initialFormData}
    >
      <SectionCard id={`${TOP_LEVEL_ID}__details-card`}>
        <ChakraFlex
          direction="column"
          justifyContent="space-between"
          gap="24px"
          width="100%"
        >
          <ChakraFlex
            alignItems="center"
            justifyContent="space-between"
            marginBottom="15px"
            gap="12px"
          >
            {getGroupClassIconElement(groupIcon, '64px', '64px')}
            <Tag
              id={`${TOP_LEVEL_ID}__tag`}
              color={groupTypeTagColor}
              value={groupTypeCode}
            />
          </ChakraFlex>

          <ChakraFlex direction="column" gap="12px">
            <Text variant="h4">{title}</Text>
            <Text variant="body" color="black.70">
              {description}
            </Text>
          </ChakraFlex>

          <ChakraDivider colorScheme="line" id={`${TOP_LEVEL_ID}__divider`} />

          <RegistrationCriteria
            formState={initialFormData}
            isReadOnly={true}
            placeholder="Not Set"
          />

          <ChakraFlex gap="8px">
            {isCore && (
              <Tag
                value="Core"
                id={`${TOP_LEVEL_ID}__core_tag`}
                color={TagColor.white}
                isBorderRequired={true}
              />
            )}
            {isSensitive && (
              <Tag
                value="Sensitive"
                id={`${TOP_LEVEL_ID}__sensitive_tag`}
                color={TagColor.white}
                isBorderRequired={true}
              />
            )}
          </ChakraFlex>
        </ChakraFlex>
      </SectionCard>
    </SkeletonLoader>
  );
};

export default GroupDetailsCard;
